import type { ICountryData } from "@nvb/ui";

/**
 * The country list every form on this site renders, taken from the backoffice
 * guide (`guide_countries`) instead of the copy bundled with `@nvb/ui`.
 *
 * Why it matters beyond tidiness: the backoffice validates what these forms
 * submit against that same guide, so a country present in one list and absent
 * from the other is a form a visitor can fill in and a backoffice that files it
 * wrong. The dial codes and masks behind the phone field come from the same
 * row.
 *
 * Delivery is the site's usual one — sync-worker writes the endpoint's response
 * to a JSON file, nginx serves it under the public-proxy path, the browser
 * reads it. No live call to the backoffice, and nothing new in the bundle: the
 * list arrives after the first paint and replaces the bundled one, which is
 * identical data until the guide is edited.
 */

/** Row shape of `GET /v1/dictionary/global-countries` (DictionaryService). */
interface IBackofficeCountry {
	id?: number;
	name?: string | null;
	full_name?: string | null;
	iso?: string | null;
	code?: string | null;
	masks?: string[] | string | null;
	flag?: string | null;
}

const COUNTRIES_PATH = "dictionary/global-countries";

/** Offset from ASCII 'A' to the regional indicator symbol block. */
const REGIONAL_INDICATOR_OFFSET = 0x1f1e6 - 0x41;

/**
 * Flag emoji for an ISO 3166-1 alpha-2 code.
 *
 * Derived rather than looked up: the guide stores a flag IMAGE URL in its
 * `flag` column (it was seeded from the same `phone_codes.json` the bundled
 * list came from), and the selects render `${flag} ${name}` as text — dropping
 * the URL in there would print the URL. The emoji is a pure function of the
 * ISO code the guide does hold, so nothing here falls back to bundled data.
 */
function flagEmojiFromIso(iso: string): string {
	if (!/^[A-Za-z]{2}$/.test(iso)) return "";

	return [...iso.toUpperCase()]
		.map((letter) => String.fromCodePoint(letter.charCodeAt(0) + REGIONAL_INDICATOR_OFFSET))
		.join("");
}

function toMasks(masks: IBackofficeCountry["masks"]): string[] | undefined {
	if (Array.isArray(masks)) return masks.filter((mask): mask is string => typeof mask === "string" && mask !== "");
	if (typeof masks === "string" && masks !== "") return [masks];

	return undefined;
}

/**
 * Map one guide row onto what the library renders.
 *
 * A row with no ISO or no name is dropped: both key everything downstream, and
 * `@nvb/ui` drops such rows anyway. A missing dial code is kept as an empty
 * string — `guide_countries.code` is nullable and those territories still
 * belong in a country select.
 */
export function mapBackofficeCountries(payload: unknown): ICountryData[] {
	const rows = (payload as { data?: unknown } | null)?.data;

	if (!Array.isArray(rows)) return [];

	const mapped: ICountryData[] = [];

	for (const row of rows as IBackofficeCountry[]) {
		const iso = typeof row?.iso === "string" ? row.iso.trim().toUpperCase() : "";
		const name = typeof row?.name === "string" ? row.name.trim() : "";

		if (!iso || !name) continue;

		const flagUrl = typeof row.flag === "string" && row.flag.startsWith("http") ? row.flag : undefined;
		const masks = toMasks(row.masks);

		mapped.push({
			name,
			iso,
			code: typeof row.code === "string" ? row.code.trim() : "",
			flag: flagEmojiFromIso(iso),
			...(flagUrl ? { flagUrl } : {}),
			...(masks ? { masks } : {}),
		});
	}

	return mapped;
}

/**
 * Base the browser reads the pre-generated JSON from.
 *
 * Same origin on purpose, exactly like `head.tsx`: nginx intercepts the path
 * on whichever host the bundle is deployed to, and an empty prefix yields a
 * root-relative URL.
 */
function apiBase(): string {
	const configured = (globalThis as { __NVB_CONFIG__?: { apiBaseUrl?: string } }).__NVB_CONFIG__?.apiBaseUrl;

	return configured || "/api/v1/public-proxy/";
}

/**
 * Fetch the guide and hand it to `@nvb/ui`.
 *
 * Failure is silent by design: the library keeps the list it already has (the
 * bundled one), which is a working form rather than an empty country select.
 * Never throws, so a caller can fire it and forget it.
 *
 * `@nvb/ui` is imported here rather than at the top of the file so that
 * {@link mapBackofficeCountries} — the part worth unit-testing — stays a pure
 * module. A static import pulls the library's built `dist/index.js` into the
 * test run, where Node's ESM resolver rejects its extensionless
 * `crypto-js/sha256` import and the whole spec file fails to load. In the
 * browser the library is already in the graph (the page wrapper imports it),
 * so this resolves to the module that is loaded either way.
 */
export async function refreshCountries(): Promise<void> {
	if (typeof window === "undefined") return;

	try {
		const response = await fetch(`${apiBase()}${COUNTRIES_PATH}`, { headers: { Accept: "application/json" } });

		if (!response.ok) return;

		const countries = mapBackofficeCountries(await response.json());

		if (countries.length === 0) return;

		const { setCountries } = await import("@nvb/ui");

		setCountries(countries);
	} catch {
		// Keep whatever list is already in place.
	}
}
